import React, { useState, useEffect } from 'react';
import { Head } from './Head';
import Body from '@teetsh/app/src/containers/EmploiDuTemps/Print/Body';
import {
  computeHeaderRows,
  computeHeights,
} from '@teetsh/app/src/containers/EmploiDuTemps/Print/utils';
import { computeTable } from '@teetsh/app/src/containers/printUtils/computeTable';
import { getNormalizedWorkingDays } from 'containers/printUtils/getNormalizedWorkingDays';
import { getNormalizedDay } from 'containers/printUtils/getNormalizedDay';
import {
  EDTEvent,
  EDTPDFSettings,
  EmploiDuTempsPrint,
} from '@teetsh/app/src/types/edt';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { filterEventByClassroomAndDay } from '@teetsh/app/src/containers/EmploiDuTemps/Print/filterEventByClassroomAndDay';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import { useIsBelowBreakpoint } from '@teetsh/app/src/components/ResponsiveSwitcher/useIsBelowSize';
import { ClassroomsOrg } from '@teetsh/app/src/types/school';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import SizeAware from 'containers/EmploiDuTemps/Print/SizeAware';

interface Props {
  settings: EDTPDFSettings;
  classrooms: Classroom[];
  emploiDuTemps: EmploiDuTempsPrint;
  maxHeight: number;
}

const StyledEDTTable = styled.table`
  table-layout: fixed;
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;
  ${tw`rounded-t-lg`}
  ${tw`rounded-b-lg`}
  border-style: hidden;
  box-shadow: 0 0 0 1px black;

  td,
  th {
    padding: 0 !important;
    border: 1px solid black;
  }

  thead > tr:last-of-type > th {
    border-bottom: 0 solid transparent !important;
  }

  tr:last-child {
    td,
    th {
      padding: 0 !important;
      border-bottom: 0 solid black;
    }
  }

  thead th {
    ${tw`bg-neutral-quiet`}
  }

  thead > tr:first-child > th:first-child {
    ${tw`rounded-tl-lg`}
  }

  thead > tr:first-child > th:last-child {
    ${tw`rounded-tr-lg`}
  }

  // Force header to be visible
  thead {
    display: table-header-group !important;
    visibility: visible !important;
    height: auto !important;
  }

  // Force rows to be visible
  tr {
    display: table-row !important;
    visibility: visible !important;
    height: auto !important;
  }

  // Force cells to be visible
  th,
  td {
    display: table-cell !important;
    visibility: visible !important;
    height: auto !important;
  }
`;

interface TableProps {
  settings: EDTPDFSettings;
  classrooms: Classroom[];
  maxHeight: number;
  events: EDTEvent[];
  days: number[];
  classroomsOrg: ClassroomsOrg;
}

function Table(props: TableProps) {
  const { events, classrooms, settings, classroomsOrg, maxHeight, days } =
    props;
  const { locale } = useUser();
  const [isHeaderRendered, setIsHeaderRendered] = useState(false);
  const [titleSpaceSize, setTitleSpaceSize] = useState(30); // Default size to prevent 0

  // Compute header rows
  const [fRow, sRow] = computeHeaderRows(
    events,
    days,
    classrooms,
    classroomsOrg,
    locale,
  );

  // Validate header data
  useEffect(() => {
    // Log header data to help debug
    // eslint-disable-next-line no-console
    console.log('Day row cells:', fRow.cells.length, fRow);
    // eslint-disable-next-line no-console
    console.log('Classroom row cells:', sRow.cells.length, sRow);

    // Set header as rendered with a delay
    const timer = setTimeout(() => {
      setIsHeaderRendered(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [fRow, sRow]);

  // Compute table rows
  const rows = computeTable(events, classrooms, days, classroomsOrg);

  // Only compute heights once we have a titleSpaceSize
  const rowsWithHeight = computeHeights(
    rows,
    // Ensure we never have a negative or too small value
    Math.max(100, maxHeight - Math.max(30, titleSpaceSize)),
    settings,
  );

  // Handle header size measurement
  const handleHeaderSize = size => {
    // eslint-disable-next-line no-console
    console.log('Header size measured:', size);
    // Ensure we never set a 0 or negative size
    const newSize = Math.max(30, Math.trunc(size) + 5); // Added more safety margin
    setTitleSpaceSize(newSize);
  };

  return (
    <>
      <StyledEDTTable>
        {/* Measure header size only after render */}
        {isHeaderRendered ? (
          <SizeAware setSize={handleHeaderSize}>
            <Head dayRow={fRow} settings={settings} classroomRow={sRow} />
          </SizeAware>
        ) : (
          <Head dayRow={fRow} settings={settings} classroomRow={sRow} />
        )}

        <Body
          classrooms={classrooms}
          classroomsOrg={classroomsOrg}
          settings={settings}
          rows={rowsWithHeight}
        />
      </StyledEDTTable>
    </>
  );
}

export function EDTTable(props: Props) {
  const { settings, classrooms, emploiDuTemps } = props;
  const isMobile = useIsBelowBreakpoint('md');

  const events = filterEventByClassroomAndDay(emploiDuTemps.events, classrooms);
  let days = getNormalizedWorkingDays(classrooms);

  if (settings.selectedDays && settings.selectedDays.length > 0) {
    const normalizedSelectedDays = settings.selectedDays.map(day =>
      getNormalizedDay(day),
    );
    days = days.filter(day => normalizedSelectedDays.includes(day));
  }
  if (isMobile) {
    return (
      <div tw="space-y-8">
        {days.map(d => (
          <Table
            key={d}
            settings={settings}
            classroomsOrg={emploiDuTemps.classroomsOrg}
            classrooms={classrooms}
            maxHeight={props.maxHeight}
            events={events}
            days={[d]}
          />
        ))}
      </div>
    );
  }

  return (
    <Table
      settings={settings}
      classroomsOrg={emploiDuTemps.classroomsOrg}
      classrooms={classrooms}
      maxHeight={props.maxHeight}
      events={events}
      days={days}
    />
  );
}
